import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap, BehaviorSubject, Subject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoginRequest, LoginResponse, User, Workflow } from '../models/user.model';
import { UserPreferences, DEFAULT_GRID_PREFERENCES } from '../models/preferences.model';
import { TaskStatus } from '../models/task.model';
import { environment } from '../../environments/environment';
import { StorageService, STORAGE_KEYS } from '../core/services/storage.service';
import * as CryptoJS from 'crypto-js';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly apiUrl = environment.apiUrl;

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  /** Notifica a otros servicios/guards cuando se fuerza logout (token inválido, 401...). */
  private forcedLogoutSubject = new Subject<void>();
  public forcedLogout$ = this.forcedLogoutSubject.asObservable();

  constructor() {
    this.loadStoredUser();
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    const email = (credentials.email || '').trim().toLowerCase();
    // El backend espera SHA-256 (mantener compatibilidad). Nota: el hashing
    // real con sal/argon2 debe ocurrir en el servidor; aquí solo se evita
    // enviar la contraseña en claro por la red.
    const hashedPassword = CryptoJS.SHA256(credentials.password).toString();

    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, {
      email,
      password: hashedPassword
    }).pipe(
      tap(response => {
        this.storeToken(response.token);
        this.storeExpiresAt(response.expires_at);
        this.storeUser(response.user);
        this.storeWorkflows(response.workflows);
      })
    );
  }

  // ── Recordar usuario / contraseña de sesión ───────────────

  rememberEmail(email: string): void {
    this.storage.set(STORAGE_KEYS.rememberedEmail, email);
  }

  forgetEmail(): void {
    this.storage.remove(STORAGE_KEYS.rememberedEmail);
  }

  getRememberedEmail(): string | null {
    return this.storage.get(STORAGE_KEYS.rememberedEmail);
  }

  saveSessionPassword(password: string): void {
    this.storage.set(STORAGE_KEYS.sessionPassword, password);
  }

  getSessionPassword(): string | null {
    return this.storage.get(STORAGE_KEYS.sessionPassword);
  }

  refreshToken(): Observable<LoginResponse> {
    const token = this.getToken();
    const headers = new HttpHeaders(token ? { Authorization: `Bearer ${token}` } : {});
    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/refresh`, {}, { headers }).pipe(
      tap(response => {
        this.storeToken(response.token);
        this.storeExpiresAt(response.expires_at);
        this.storeUser(response.user);
        this.storeWorkflows(response.workflows);
      })
    );
  }

  isTokenExpired(): boolean {
    const expiresAt = this.storage.get(STORAGE_KEYS.expiresAt);
    if (!expiresAt) return true;
    const expirationDate = new Date(expiresAt);
    if (isNaN(expirationDate.getTime())) return true;
    // Margen de 30 s para evitar usar un token a punto de expirar
    return Date.now() >= expirationDate.getTime() - 30_000;
  }

  logoutFromBackend(): Observable<void> {
    const token = this.getToken();
    if (!token) {
      this.clearLocalData();
      return of(void 0);
    }
    return this.http.post<void>(`${this.apiUrl}/auth/logout`, {}).pipe(
      tap(() => this.clearLocalData()),
      catchError(() => {
        // Aunque falle el backend, limpiamos el estado local
        this.clearLocalData();
        return of(void 0);
      })
    );
  }

  logout(): void {
    this.clearLocalData();
  }

  /** Logout forzado (token expirado / 401). Emite forcedLogout$ para que la UI reaccione. */
  forceLogout(): void {
    this.clearLocalData();
    this.forcedLogoutSubject.next();
  }

  private clearLocalData(): void {
    this.storage.clearAll();
    this.currentUserSubject.next(null);
  }

  getToken(): string | null {
    return this.storage.get(STORAGE_KEYS.token);
  }

  isLoggedIn(): boolean {
    return !!this.getToken() && !this.isTokenExpired();
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  isAdmin(): boolean {
    return !!this.getCurrentUser()?.is_admin;
  }

  private storeToken(token: string): void {
    this.storage.set(STORAGE_KEYS.token, token);
  }

  private storeExpiresAt(expiresAt: string): void {
    this.storage.set(STORAGE_KEYS.expiresAt, expiresAt);
  }

  private storeUser(user: User): void {
    this.storage.setJSON(STORAGE_KEYS.user, user);
    this.currentUserSubject.next(user);
  }

  storeWorkflows(workflows: Workflow[]): void {
    const sorted = [...workflows].sort((a, b) => a.order - b.order);
    this.storage.setJSON(STORAGE_KEYS.workflows, sorted);
  }

  private loadStoredUser(): void {
    const user = this.storage.getJSON<User | null>(STORAGE_KEYS.user, null);
    if (user) this.currentUserSubject.next(user);
  }

  setUser(user: User): void {
    this.storeUser(user);
  }

  getWorkflows(): Workflow[] {
    const workflows = this.storage.getJSON<Workflow[]>(STORAGE_KEYS.workflows, []);
    return [...workflows].sort((a, b) => a.order - b.order);
  }

  // ── Caché de estados ──────────────────────────────────────
  storeStatuses(statuses: TaskStatus[]): void {
    this.storage.setJSON(STORAGE_KEYS.statuses, statuses);
  }

  getCachedStatuses(): TaskStatus[] {
    return this.storage.getJSON<TaskStatus[]>(STORAGE_KEYS.statuses, []);
  }

  // ── Preferencias ──────────────────────────────────────────
  getPreferences(): UserPreferences {
    return this.storage.getJSON<UserPreferences>(
      STORAGE_KEYS.preferences,
      { grid: { columns: DEFAULT_GRID_PREFERENCES, viewMode: 'grid' } }
    );
  }

  savePreferences(prefs: UserPreferences): void {
    this.storage.setJSON(STORAGE_KEYS.preferences, prefs);
  }

  getGridPreferences(): any[] {
    return this.getPreferences().grid?.columns || DEFAULT_GRID_PREFERENCES;
  }

  updateGridPreferences(columns: any[]): void {
    const prefs = this.getPreferences();
    prefs.grid = prefs.grid || { columns: DEFAULT_GRID_PREFERENCES };
    prefs.grid.columns = columns;
    this.savePreferences(prefs);
  }

  getViewMode(): 'grid' | 'cards' | 'calendar' {
    return this.getPreferences().grid?.viewMode || 'cards';
  }

  setViewMode(mode: 'grid' | 'cards' | 'calendar'): void {
    const prefs = this.getPreferences();
    prefs.grid = prefs.grid || { columns: DEFAULT_GRID_PREFERENCES };
    prefs.grid.viewMode = mode;
    this.savePreferences(prefs);
  }
}
